package sarka.design.skoria.messaging

import io.ktor.client.*
import io.ktor.client.plugins.websocket.*
import io.ktor.websocket.*
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.serialization.json.Json
import sarka.design.skoria.network.TokenStore
import sarka.design.skoria.network.createPlatformHttpClient
import sarka.design.skoria.network.defaultDmUrl
import kotlin.coroutines.cancellation.CancellationException

/**
 * Live delivery socket for direct messages.
 *
 * Only receives. Sending goes through [MessageApi] so the server can persist
 * before notifying, which means a socket drop delays messages rather than losing
 * them. On reconnect the server replays anything undelivered, so the gap closes
 * by itself.
 *
 * Unlike [sarka.design.skoria.network.ChatClient], this reconnects on its own:
 * a messaging socket is meant to stay open for the whole session, and a user
 * shouldn't have to do anything after a network blip.
 */
class DmClient(
    private val url: String = defaultDmUrl(),
    private val httpClient: HttpClient = createPlatformHttpClient(),
) {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val _incoming = MutableSharedFlow<DirectMessage>(extraBufferCapacity = 64)
    val incoming: SharedFlow<DirectMessage> = _incoming

    /** Delivery/read receipts for messages this user sent. */
    private val _receipts = MutableSharedFlow<DmFrame.Receipt>(extraBufferCapacity = 64)
    val receipts: SharedFlow<DmFrame.Receipt> = _receipts

    private val _connected = MutableStateFlow(false)
    val connected: StateFlow<Boolean> = _connected

    private var session: DefaultClientWebSocketSession? = null
    private var runner: Job? = null

    /** Idempotent: calling this when already running does nothing. */
    fun connect() {
        if (runner?.isActive == true) return

        runner = scope.launch {
            var backoffMs = 1_000L

            while (isActive) {
                val token = TokenStore.token.value
                if (token == null) {
                    // Not logged in yet. Wait rather than hammering the server
                    // with handshakes that will be rejected.
                    delay(2_000)
                    continue
                }

                try {
                    httpClient.webSocket("$url?token=$token") {
                        session = this
                        _connected.value = true
                        backoffMs = 1_000L

                        for (frame in incoming) {
                            if (frame !is Frame.Text) continue

                            val parsed = try {
                                json.decodeFromString(DmFrame.serializer(), frame.readText())
                            } catch (_: Throwable) {
                                continue
                            }

                            when (parsed) {
                                is DmFrame.Incoming -> {
                                    _incoming.emit(parsed.message)
                                    // Tell the server it landed so it isn't
                                    // replayed on the next reconnect.
                                    ack(parsed.message.id)
                                }
                                is DmFrame.Receipt -> _receipts.emit(parsed)
                                is DmFrame.Ready -> Unit
                                is DmFrame.Error -> Unit
                                is DmFrame.Ack -> Unit
                            }
                        }
                    }
                } catch (_: CancellationException) {
                    throw CancellationException()
                } catch (_: Throwable) {
                    // Fall through to the backoff below.
                } finally {
                    session = null
                    _connected.value = false
                }

                if (!isActive) break

                delay(backoffMs)
                // Cap the backoff so a long outage doesn't leave the client
                // waiting minutes after the server comes back.
                backoffMs = (backoffMs * 2).coerceAtMost(30_000L)
            }
        }
    }

    private suspend fun ack(messageId: String) {
        val current = session ?: return
        try {
            current.send(Frame.Text(json.encodeToString(DmFrame.serializer(), DmFrame.Ack(messageId))))
        } catch (_: Throwable) {
            // Losing an ack only means the message is replayed later, which the
            // client deduplicates by id.
        }
    }

    fun disconnect() {
        runner?.cancel()
        runner = null
        scope.launch { session?.close() }
    }

    fun dispose() {
        runner?.cancel()
        scope.cancel()
        httpClient.close()
    }
}
